import React, {useCallback, useEffect} from 'react';
import {Stack} from '@chakra-ui/react';
import {useDispatch, useSelector} from 'react-redux';
import {useSwiper} from 'swiper/react';
import CustomDropdown from '~/components/dropdowns/CustomDropdown';
import {UiBox, UiInput, UiFormControl, UiFormErrorMessage, Col} from '~/components/elements';
import CustomRadio from '~/components/elements/CustomRadio';
import {DATA_CHECK_BOX, NO_OPTION, PRODUCTS, QUESTIONS, QUESTIONS_PARAMS, YES_OPTION} from '~/constants/popup';
import {useInput} from '~/hooks';
import {RootState} from '~/redux/root-reducer';
import {POP_UP_PH_CHECKOUT_KEYS} from '~/constants/popup';
import {MedicalQuestionnaireKey} from '~/redux/questionnaires/types';
import {BackAndNextSlide, TitlePopup, PopupLayout} from '~/pages/homes/pop-up/components';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {ChakraStylesConfig} from 'chakra-react-select';

const chakraStyles: ChakraStylesConfig = {
  control: (provided: any, state: any) => ({
    ...provided,
    minWidth: '10em',
    fontWeight: 'normal',
    textTransform: 'none',
    height: '48px',
    borderRadius: '8px',
  }),
  menuList: (provided: any, state: any) => ({
    ...provided,
    fontWeight: 'normal',
    textTransform: 'none',
    overflowX: 'hidden',
    maxHeight: '200px',
  }),
};

export const PopupOne = () => {
  const swiper = useSwiper();
  const dispatch = useDispatch();

  const slideKey = useSelector((state: RootState) => state.questionnaires.nextSlideKey);
  const answer = useSelector((state: RootState) => state.questionnaires.collectData);
  const isGoback = !!answer[slideKey as MedicalQuestionnaireKey];

  const questionnaire = answer?.[POP_UP_PH_CHECKOUT_KEYS.USED_BIRTH_CONTROL];
  const {data: answerData} = questionnaire || {};

  const defaultValue = {
    selectedOption: answerData?.selectedOption || '',
    brandUsed: answerData?.brandUsed || '',
    issues: answerData?.issues || '',
  };

  const rules = {
    issues: function () {
      if (inputs.selectedOption === YES_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {maximum: 300, message: '^This field is too long (maximum is 300 characters)'},
        };
      } else {
        return {presence: false};
      }
    },
  };

  const {inputs, setInputs, trimSpaces, validation} = useInput(defaultValue, {rules});

  const isNextSlide =
    inputs.selectedOption === YES_OPTION
      ? Object.values(inputs).every(el => !!el)
      : inputs.selectedOption === NO_OPTION;

  const handleNextSlide = useCallback(() => {
    if (validation.getErrors()) return;

    const data = inputs.selectedOption === YES_OPTION ? inputs : {selectedOption: NO_OPTION};
    dispatch(
      setQuestionnaireAnswer({
        key: POP_UP_PH_CHECKOUT_KEYS.USED_BIRTH_CONTROL,
        index: 'SCREEN 1',
        data,
        questions: QUESTIONS_PARAMS.USED_BIRTH_CONTROL,
      }),
    );
    setTimeout(() => {
      swiper.allowSlideNext = true;
      swiper.slideNext();
    }, 200);
  }, [dispatch, inputs, swiper, validation]);

  useEffect(() => {
    if (slideKey === POP_UP_PH_CHECKOUT_KEYS.USED_BIRTH_CONTROL) {
      if (isNextSlide) {
        if (validation?.errors?.issues || inputs.selectedOption === YES_OPTION) {
          swiper.allowSlideNext = false;
          return;
        }
        swiper.allowSlideNext = true;
        if (isGoback) return;
        if (inputs.selectedOption === NO_OPTION) {
          handleNextSlide();
        }
      } else {
        swiper.allowSlideNext = false;
      }
    }
  }, [handleNextSlide, inputs.selectedOption, isGoback, isNextSlide, slideKey, swiper, validation?.errors?.issues]);

  const handleClickOptions = (value: string) => {
    setInputs({selectedOption: value});
  };

  return (
    <>
      <BackAndNextSlide onNextSlide={handleNextSlide} isActiveNextSlide={isNextSlide} />
      <PopupLayout>
        <TitlePopup>{QUESTIONS.USED_BIRTH_CONTROL.selectedOption}</TitlePopup>
        <Stack direction="row" my={4}>
          {DATA_CHECK_BOX.map(val => (
            <CustomRadio
              style={{marginRight: 40, cursor: 'pointer'}}
              key={val.label}
              text={val.label}
              checked={inputs.selectedOption === val.label}
              onClick={() => handleClickOptions(val.label)}
            />
          ))}
        </Stack>
        {inputs.selectedOption === YES_OPTION && (
          <>
            <TitlePopup>{QUESTIONS.USED_BIRTH_CONTROL.brandUsed}</TitlePopup>
            <UiBox>
              <CustomDropdown
                className="swiper-no-swiping"
                selectedFontWeight="600"
                options={PRODUCTS}
                onChange={item => setInputs({brandUsed: item?.label})}
                defaultItem={PRODUCTS.find(el => el.label === inputs.brandUsed)}
                chakraStyles={chakraStyles}
              />
            </UiBox>
            <UiFormControl id="issues" isRequired isInvalid={!!validation?.errors?.issues}>
              <UiBox>
                <TitlePopup>{QUESTIONS.USED_BIRTH_CONTROL.issues}</TitlePopup>
                <UiBox>
                  <UiInput
                    value={inputs.issues}
                    onChange={({target}) => setInputs({issues: target.value})}
                    height="48px"
                    borderRadius="8px"
                  />
                </UiBox>
                {validation?.errors?.issues && (
                  <UiFormErrorMessage px={1}>{validation?.errors?.issues[0]}</UiFormErrorMessage>
                )}
              </UiBox>
            </UiFormControl>
          </>
        )}
      </PopupLayout>
    </>
  );
};
